import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { ArrowLeft } from "lucide-react";

const MIN_TICKET_PRICE = 100;

// 5% per ticket in total: 2% Razorpay + 3% Growithevent.
const TOTAL_FEE_PERCENT = 5;

function totalFeePerTicket(price) {
  return Math.round(price * TOTAL_FEE_PERCENT) / 100;
}

export default function CreateEvent() {
  const [form, setForm] = useState({
    name: "",
    date: "",
    location: "",
    capacity: "",
    price: "",
  });
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const price = parseFloat(form.price);
  const hasValidPrice = Number.isFinite(price) && price >= MIN_TICKET_PRICE;
  const feePerTicket = hasValidPrice ? totalFeePerTicket(price) : 0;
  const payoutPerTicket = hasValidPrice ? Math.max(0, price - feePerTicket) : 0;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!hasValidPrice) {
      toast.error("Minimum ticket price is ₹100");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        date: new Date(form.date).toISOString(),
        location: form.location,
        capacity: parseInt(form.capacity, 10),
        price: parseFloat(form.price),
      };
      const { data } = await api.post("/events", payload);
      toast.success("Event created");
      nav(`/organizer/events/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <nav className="bg-white border-b border-zinc-200">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <Link
            to="/organizer/dashboard"
            className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-black transition-colors"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </Link>
        </div>
      </nav>

      <main className="max-w-2xl mx-auto px-6 py-12">
        <p className="overline mb-3">Create</p>
        <h1 className="font-heading text-4xl font-semibold tracking-tighter mb-10">
          New event
        </h1>

        <form onSubmit={submit} className="space-y-6" data-testid="create-event-form">
          <div>
            <Label className="text-sm font-medium mb-2 block">Event name</Label>
            <Input
              required
              value={form.name}
              onChange={set("name")}
              placeholder="Annual Meetup 2026"
              className="h-11 rounded-md border-zinc-200 focus-visible:ring-black"
              data-testid="event-name-input"
            />
          </div>

          <div>
            <Label className="text-sm font-medium mb-2 block">Date & time</Label>
            <Input
              type="datetime-local"
              required
              value={form.date}
              onChange={set("date")}
              className="h-11 rounded-md border-zinc-200 focus-visible:ring-black"
              data-testid="event-date-input"
            />
          </div>

          <div>
            <Label className="text-sm font-medium mb-2 block">
              Location <span className="text-zinc-400 font-normal">(optional)</span>
            </Label>
            <Input
              value={form.location}
              onChange={set("location")}
              placeholder="Bangalore · Venue TBA"
              className="h-11 rounded-md border-zinc-200 focus-visible:ring-black"
              data-testid="event-location-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">
                Capacity (cap)
              </Label>
              <Input
                type="number"
                min="1"
                required
                value={form.capacity}
                onChange={set("capacity")}
                placeholder="100"
                className="h-11 rounded-md border-zinc-200 focus-visible:ring-black"
                data-testid="event-capacity-input"
              />
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">
                Price (₹)
              </Label>
              <Input
                type="number"
                min="100"
                step="0.01"
                required
                value={form.price}
                onChange={set("price")}
                placeholder="499"
                className="h-11 rounded-md border-zinc-200 focus-visible:ring-black"
                data-testid="event-price-input"
              />
              <div className="mt-2 space-y-1.5 text-xs">
                <p className="text-zinc-600">
                  Minimum ticket price: <span className="font-medium text-black">₹100</span>
                </p>
                <p className="text-zinc-600" data-testid="event-fee-note">
                  Fees: <span className="font-medium text-black">5% per ticket</span> (2% Razorpay + 3% Growithevent).
                  {hasValidPrice && (
                    <>
                      {" "}You receive <span className="font-semibold text-black">₹{payoutPerTicket}</span> per ticket.
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>

          <div className="pt-4">
            <Button
              type="submit"
              disabled={loading}
              className="h-11 bg-black text-white hover:bg-zinc-800 rounded-md px-6"
              data-testid="create-event-submit-button"
            >
              {loading ? "Creating..." : "Create event"}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
